import type { ComparisonCategory, PeerSearchPlace, SampleConfidence } from '@/types/gmb';

interface PeerPreviewSectionProps {
  /** 是否正在進行同業搜尋 */
  loading: boolean;
  /** 載入中顯示的說明（一般實體 / 服務區域不同） */
  loadingMessage: string;
  /** 同業清單（成功時才會有值） */
  peers: PeerSearchPlace[] | null;
  /** 比較模式：'NEARBY' 一般實體 / 'SERVICE_AREA' 服務區域 */
  mode: string | null;
  /** 本次 Nearby 搜尋最終使用的半徑（公尺）；服務區域為 null */
  radiusUsedM: number | null;
  /** 服務區域比對範圍（例如：台中）；Nearby 為 null */
  comparisonArea: string | null;
  /** 同業樣本總數 */
  sampleCount: number | null;
  /** 樣本可信度 */
  sampleConfidence: SampleConfidence | null;
  /** 同業分類決策結果（顯示用） */
  comparisonCategory: ComparisonCategory | null;
  /** 同業挑選方式（KEYWORD_TEXT_SEARCH 時空狀態文案不同） */
  selectionMode: string | null;
  /** 點擊「修改同業分類」（僅 USER_CONFIRMED 顯示；不重新搜尋店家） */
  onModifyCategory?: () => void;
  /** 有提供 rating 的 peer 數 */
  ratingSampleCount: number | null;
  /** 有提供 userRatingCount 的 peer 數 */
  reviewCountSampleCount: number | null;
  /** 同業 rating 中位數；無有效樣本為 null */
  ratingMedian: number | null;
  /** 同業評論數中位數；無有效樣本為 null */
  reviewCountMedian: number | null;
  /** 同業搜尋失敗時的訊息 */
  errorMessage: string | null;
  /** 未執行同業搜尋時的低調說明（資料不足等） */
  noticeMessage: string | null;
}

/** distanceM < 1000 → 「650 公尺」；>= 1000 → 「1.8 公里」。 */
function formatPeerDistance(meters?: number): string {
  if (meters === undefined || meters === null || Number.isNaN(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)} 公尺`;
  return `${(meters / 1000).toFixed(1)} 公里`;
}

function formatRadius(meters: number | null): string {
  if (meters === null || Number.isNaN(meters)) return '—';
  return `${(meters / 1000).toFixed(1)} 公里`;
}

/** 樣本數量中文顯示（Backend 保留 enum，前端只做 UI translation）。 */
function formatSampleAmount(confidence: SampleConfidence | null): string {
  switch (confidence) {
    case 'HIGH':
      return '充足';
    case 'MEDIUM':
      return '尚可';
    case 'LOW':
      return '偏少';
    case 'NONE':
      return '不足';
    default:
      return '—';
  }
}

/** 同業分類顯示文字（不顯示技術 enum）。 */
function formatCategoryLabel(category: ComparisonCategory | null): string | null {
  if (!category || !category.comparisonLabel) return null;
  switch (category.mode) {
    case 'USER_CONFIRMED':
      return `${category.comparisonLabel}（已確認）`;
    case 'GOOGLE_TYPES_FALLBACK':
    case 'GOOGLE_PRIMARY_TYPE':
      return `${category.comparisonLabel}（Google 類型）`;
    default:
      return category.comparisonLabel;
  }
}

function PeerCard({ peer, showDistance }: { peer: PeerSearchPlace; showDistance: boolean }) {
  return (
    <div className="rounded-2xl border border-background-300 bg-background-50 p-4 md:p-5">
      <p className="truncate text-base font-bold text-foreground-950">{peer.displayName || '未命名商家'}</p>
      {peer.primaryTypeDisplayName && (
        <p className="mt-1 truncate text-sm text-foreground-600">{peer.primaryTypeDisplayName}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-base text-foreground-700">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <i className="ri-star-fill text-lg text-accent-500" />
          {peer.rating === undefined ? '—' : peer.rating.toFixed(1)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-chat-3-line text-lg" />
          {peer.userRatingCount === undefined ? '—' : peer.userRatingCount.toLocaleString('zh-TW')} 則評論
        </span>
        {showDistance && (
          <span className="inline-flex items-center gap-1.5 text-foreground-500">
            <i className="ri-route-line" />
            {formatPeerDistance(peer.distanceM)}
          </span>
        )}
      </div>
    </div>
  );
}

function SampleStat({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-primary-200/70 bg-background-50 px-4 py-3">
      <i className={`${icon} text-lg text-primary-700`} />
      <div className="min-w-0">
        <p className="text-sm leading-tight text-primary-800/80">{label}</p>
        <p className="text-base font-bold leading-tight text-primary-900">{value}</p>
      </div>
    </div>
  );
}

/** Snapshot 成功後：同業搜尋的載入 / 成功 / 失敗 / 略過狀態（一般實體 + 服務區域）。 */
export default function PeerPreviewSection({
  loading,
  loadingMessage,
  peers,
  mode,
  radiusUsedM,
  comparisonArea,
  sampleCount,
  sampleConfidence,
  ratingSampleCount,
  reviewCountSampleCount,
  ratingMedian,
  reviewCountMedian,
  comparisonCategory,
  selectionMode,
  onModifyCategory,
  errorMessage,
  noticeMessage,
}: PeerPreviewSectionProps) {
  if (loading) {
    return (
      <section className="ds-container pt-6">
        <div className="rounded-2xl border border-background-300 bg-background-50 p-5 md:p-6">
          <div className="flex items-center gap-4">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-700" />
            <div className="min-w-0">
              <p className="text-base font-bold text-foreground-900">{loadingMessage}</p>
              <p className="mt-1 text-base text-foreground-600">以相同主要類別建立可比較的同業樣本。</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-background-200" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (errorMessage) {
    return (
      <section className="ds-container pt-6">
        <div className="flex items-start gap-4 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-800 md:p-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50/80">
            <i className="ri-error-warning-line text-xl" />
          </span>
          <div className="min-w-0">
            <p className="text-base font-bold md:text-lg">無法完成同業搜尋</p>
            <p className="mt-1.5 text-base leading-relaxed opacity-90">{errorMessage}</p>
            <p className="mt-3 flex items-start gap-2 text-base font-medium">
              <i className="ri-arrow-right-circle-line mt-0.5 text-lg" />
              <span>請稍後再試一次；若持續失敗，可回到上方重新搜尋店家。</span>
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (noticeMessage) {
    return (
      <section className="ds-container pt-6">
        <div className="flex items-start gap-4 rounded-2xl border border-background-300 bg-background-100 p-5 text-foreground-700 md:p-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50 ring-1 ring-background-300">
            <i className="ri-information-line text-xl" />
          </span>
          <p className="text-base">{noticeMessage}</p>
        </div>
      </section>
    );
  }

  if (!peers) return null;

  const isServiceArea = mode === 'SERVICE_AREA';
  const isKeyword = selectionMode === 'KEYWORD_TEXT_SEARCH';
  const preview = peers.slice(0, 5);
  const total = sampleCount ?? peers.length;
  const isEmpty = total === 0;
  const categoryLabel = formatCategoryLabel(comparisonCategory);

  return (
    <section className="ds-container pt-8">
      <div className="rounded-2xl border border-primary-200 bg-primary-50/70 p-5 md:p-6">
        <p className="flex flex-wrap items-center gap-2 text-base font-bold text-primary-900 md:text-lg">
          <i className="ri-checkbox-circle-line text-xl" />
          {isServiceArea ? '同業樣本建立成功' : '同業搜尋成功'}
        </p>

        <div className="mt-4 flex flex-wrap gap-2.5">
          {isServiceArea ? (
            <SampleStat
              icon="ri-links-line"
              label="比較方式"
              value={comparisonArea ? `服務區域 · ${comparisonArea}` : '服務區域'}
            />
          ) : (
            <SampleStat icon="ri-links-line" label="比較方式" value="附近同業" />
          )}

          {!isServiceArea && (
            <SampleStat icon="ri-focus-3-line" label="搜尋半徑" value={formatRadius(radiusUsedM)} />
          )}

          <SampleStat icon="ri-group-line" label="有效同業" value={`${total} 家`} />
          <SampleStat icon="ri-shield-check-line" label="樣本數量" value={formatSampleAmount(sampleConfidence)} />
          <SampleStat
            icon="ri-bar-chart-2-line"
            label={`同業評分中位數（${ratingSampleCount ?? 0} 家有評分）`}
            value={ratingMedian === null ? '—' : ratingMedian.toFixed(1)}
          />
          <SampleStat
            icon="ri-chat-3-line"
            label={`同業評論數中位數（${reviewCountSampleCount ?? 0} 家有評論數）`}
            value={reviewCountMedian === null ? '—' : reviewCountMedian.toLocaleString('zh-TW')}
          />
          {categoryLabel && (
            <SampleStat icon="ri-price-tag-3-line" label="同業分類" value={categoryLabel} />
          )}
        </div>

        {comparisonCategory?.mode === 'USER_CONFIRMED' && onModifyCategory && (
          <button
            type="button"
            onClick={onModifyCategory}
            className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-primary-300 bg-background-50 px-4 py-2 text-sm font-semibold text-primary-800 transition-colors hover:bg-primary-100"
          >
            <i className="ri-edit-2-line" />
            修改同業分類
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className="mt-4 rounded-2xl border border-dashed border-background-400 bg-background-100 p-6 text-center text-base text-foreground-600">
          {isKeyword
            ? '目前依此同業分類找到的公開樣本不足，您可以調整「主要同業分類」後重新建立同業樣本。'
            : isServiceArea
              ? '目前公開資料不足，無法建立可靠的同業比較樣本。'
              : '附近查無相同主要類別的同業店家。'}
        </div>
      ) : (
        <>
          <h3 className="mb-4 mt-8 text-lg text-foreground-950 md:text-xl">
            {isServiceArea ? '服務區域同業（預覽最多 5 家）' : '附近同業（預覽最多 5 家）'}
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {preview.map((peer) => (
              <PeerCard key={peer.placeId} peer={peer} showDistance={!isServiceArea} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}